import { BackwardIcon, ForwardIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { useRef, useState } from "react";
import Dropdown from "../Settings/Dropdown";

const fmt = (sec: number) => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

/**
 * Audio player with the same controls as the Investigations transcript player
 * (blue circular play, soft +-10s, seek bar, speed menu), for audio that has no
 * transcript to sit above.
 */
const AudioBar: React.FC<{ src: string }> = ({ src }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  const seek = (t: number) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = Math.min(Math.max(0, t), Number.isFinite(el.duration) ? el.duration : t);
  };

  return (
    <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onRateChange={(e) => setRate(e.currentTarget.playbackRate)}
      />
      <button
        type="button"
        className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
        onClick={() => seek(time - 10)}
        title="Back 10s"
      >
        <BackwardIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
        onClick={() => (playing ? ref.current?.pause() : ref.current?.play().catch(() => {}))}
        title={playing ? "Pause" : "Play"}
      >
        {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
      </button>
      <button
        type="button"
        className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
        onClick={() => seek(time + 10)}
        title="Forward 10s"
      >
        <ForwardIcon className="h-4 w-4" />
      </button>
      <span className="text-sm tabular-nums text-base-content/70">
        {fmt(time)} / {fmt(duration)}
      </span>
      <input
        type="range"
        min={0}
        max={duration || 1}
        step={0.1}
        value={Math.min(time, duration || 1)}
        onChange={(e) => seek(Number(e.target.value))}
        className="inv-seek flex-1 min-w-32"
        style={{ "--p": `${duration ? (time / duration) * 100 : 0}%` } as React.CSSProperties}
      />
      <Dropdown
        size="xs"
        contentClassName="w-20"
        options={[1, 1.25, 1.5, 2].map((r) => ({ value: String(r), label: `${r}\u00d7` }))}
        value={String(rate)}
        onChange={(v) => {
          const r = Number(v);
          if (ref.current) ref.current.playbackRate = r;
          setRate(r);
        }}
      />
    </div>
  );
};

export default AudioBar;
